import { useState, useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"
import { Loader2, MailCheck, ArrowLeft } from "lucide-react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"

import { authService } from "@/features/auth/services/authService"
import { APP_NAME } from "@/lib/appConfig"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

const schema = z.object({
  email: z.string().email("Enter a valid email address"),
})

type FormValues = z.infer<typeof schema>

export default function ForgotPasswordPage() {
  const [sent, setSent] = useState(false)

  useEffect(() => {
    const prev = document.title
    document.title = `Forgot Password | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  })

  const { mutate, isPending } = useMutation({
    mutationFn: ({ email }: FormValues) => authService.forgotPassword(email),
    onSuccess: () => {
      setSent(true)
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      toast.error(message)
    },
  })

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
        <div className="w-full max-w-sm text-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center"
          >
            <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto mb-6 shadow-glow">
              <MailCheck className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight mb-3 bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Check your email
            </h2>
            <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
              If an account exists for that email, we've sent a password reset
              link. Check your inbox and spam folder.
            </p>
            <Link to="/login" className="w-full block">
              <Button variant="outline" className="w-full h-12 rounded-xl transition-all duration-200 active:scale-[0.97]">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to login
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-background">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
            Reset password
          </h1>
          <p className="text-sm text-muted-foreground mt-2">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <Card className="rounded-3xl shadow-soft border-border/50 bg-card/60 backdrop-blur-xl">
          <CardContent className="pt-6">
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((v) => mutate(v))}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="you@example.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl mt-4 shadow-glow transition-all duration-200 active:scale-[0.97]"
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Send Reset Link"
                  )}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-primary font-medium hover:underline"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to login
          </Link>
        </p>
      </div>
    </div>
  )
}
